<template>
  <div>
    <tableComponent
      ref="tableComponent"
      :tableSettings="tableSettings"
      :tableData="tableData"
      :headers="headers"
      @newDocument="newDocument"
      @downloadPdf="downloadPdf"
      @openSdsImport="openSdsImport"
      @clickedRow="$refs.detailsDialog.openDetails($event)"
      @openMultiUserDialog="openMultiUserDialog"
      @openExport="openExport"
      @openImport="openImport"
      @openBulkMove="openBulkMove"
      @confirmBulkDelete="confirmBulkDelete"
    />
    <moveDialog
      :item="bulkMove.item"
      :moveDialog="bulkMove.dialog"
      :loading="bulkMove.loading"
      :array="bulkMove.targets"
      @cancel="bulkMove.dialog.open = false"
      @confirm="handleBulkMoveConfirm"
    />
    <v-dialog v-model="bulkConfirm.open" max-width="440">
      <v-card>
        <v-card-title>{{ bulkConfirm.title }}</v-card-title>
        <v-card-text>{{ bulkConfirm.message }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="bulkConfirm.open = false"
            :disabled="bulkConfirm.loading"
          >
            {{ $t("general.cancel") }}
          </v-btn>
          <v-btn
            color="error"
            :loading="bulkConfirm.loading"
            @click="runBulkDelete"
          >
            {{ $t("dangerousGoods.dangerousGoodsList.bulk.deleteButton") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <dangerous-good-details-dialog ref="detailsDialog" />
    <dangerous-good-import-dialog
      :importDialog="importDialog"
      :loading="importing"
      @triggerUpload="importData"
    />
    <dangerous-good-export-dialog
      ref="exportDialog"
      mode="items"
      :current-storage-location-id="$route.params.storageLocationId"
    />
    <dangerous-good-sds-import-dialog
      v-if="sdsImportEnabled"
      ref="sdsImportDialog"
      :storage-location-id="$route.params.storageLocationId"
      @imported="reloadDataFunc"
    />
    <multiUserDialog
      v-model="multiUserOpen"
      entityType="dangerousGood"
      :availableItems="transferItems"
      itemText="name"
      itemValue="_id"
      :clients="transferClients"
      :currentClientId="currentClientId"
      @confirm="executeTransfer"
      entityLabel="multiUser.dialog.dangerousGoods"
    />
  </div>
</template>
<script>
import { getUniqueId } from "../../components/api/api";
import tableComponent from "../../components/_utils/dataTableHelpers/tableComponent.vue";
import DangerousGoodDetailsDialog from "@/components/tableSlotComponents/dangerousGoods/dangerousGoodsList/dangerousGoodDetailsDialog.vue";
import DangerousGoodSdsImportDialog from "@/components/dangerousGoods/DangerousGoodSdsImportDialog.vue";
import DangerousGoodExportDialog from "@/components/dangerousGoods/DangerousGoodExportDialog.vue";
import DangerousGoodImportDialog from "@/components/dangerousGoods/DangerousGoodImportDialog.vue";
import multiUserDialog from "../../components/_utils/dataTableHelpers/actions/multiUserDialog.vue";
import moveDialog from "../../components/_utils/dataTableHelpers/actions/moveDialog.vue";

const SDS_IMPORT_ENABLED = true;

export default {
  components: {
    tableComponent,
    DangerousGoodDetailsDialog,
    DangerousGoodSdsImportDialog,
    DangerousGoodExportDialog,
    DangerousGoodImportDialog,
    multiUserDialog,
    moveDialog,
  },
  computed: {
    sdsImportEnabled() {
      return SDS_IMPORT_ENABLED;
    },
    filterItems() {
      const items = this.transferItems || [];
      const uniq = (arr) => [
        ...new Set(
          arr.filter((x) => x !== undefined && x !== null && x !== "")
        ),
      ];
      const flat = (key) => uniq(items.flatMap((i) => i[key] || [])).sort();
      const scalar = (key) => uniq(items.map((i) => i[key])).sort();
      return {
        identifier: scalar("identifier"),
        name: scalar("name"),
        pictograms: flat("pictograms").map((p) => ({ text: p, value: p })),
        h: flat("h"),
        p: flat("p"),
        storageClassification: scalar("storageClassification"),
        waterHazardClass: scalar("waterHazardClass"),
        usageLocation: scalar("usageLocation"),
        manufacturer: scalar("manufacturer"),
        fss7Relevant: [
          { text: this.$t("general.yes"), value: true },
          { text: this.$t("general.no"), value: false },
        ],
      };
    },
    headers() {
      return [
        {
          text: this.$t(
            "dangerousGoods.dangerousGoodsList.tableHeader.identifier"
          ),
          value: "identifier",
          filters: [
            {
              param: "identifier",
              items: this.filterItems.identifier,
              searchable: true,
            },
          ],
        },
        {
          text: this.$t("dangerousGoods.dangerousGoodsList.tableHeader.name"),
          value: "name",
          filters: [
            { param: "name", items: this.filterItems.name, searchable: true },
          ],
        },
        {
          text: this.$t(
            "dangerousGoods.dangerousGoodsList.tableHeader.pictograms"
          ),
          value: "dangerousGoodsListPictograms",
          width: "200px",
          sortable: false,
          filters: [
            {
              param: "pictograms",
              label: this.$t(
                "dangerousGoods.dangerousGoodsList.tableHeader.pictograms"
              ),
              items: this.filterItems.pictograms,
              image: true,
            },
          ],
        },
        {
          text: this.$t(
            "dangerousGoods.dangerousGoodsList.tableHeader.hAndPSentences"
          ),
          value: "dangerousGoodsListHAndPSentences",
          width: "230px",
          sortable: false,
          filters: [
            { param: "h", label: "H-Sätze", items: this.filterItems.h },
            { param: "p", label: "P-Sätze", items: this.filterItems.p },
          ],
        },
        {
          text: this.$t(
            "dangerousGoods.dangerousGoodsList.tableHeader.storageClassification"
          ),
          value: "storageClassification",
          filters: [
            {
              param: "storageClassification",
              items: this.filterItems.storageClassification,
            },
          ],
        },
        {
          text: this.$t(
            "dangerousGoods.dangerousGoodsList.tableHeader.waterHazardClass"
          ),
          value: "waterHazardClass",
          filters: [
            {
              param: "waterHazardClass",
              items: this.filterItems.waterHazardClass,
            },
          ],
        },
        {
          text: this.$t("dangerousGoods.dangerousGoodsList.tableHeader.fss7"),
          value: "fss7Relevant",
          align: "center",
          sortable: false,
          filters: [
            { param: "fss7Relevant", items: this.filterItems.fss7Relevant },
          ],
        },
        {
          text: this.$t(
            "dangerousGoods.dangerousGoodsList.tableHeader.safetyDataSheet"
          ),
          value: "dangerousGoodsListSafetyDataSheet",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t(
            "dangerousGoods.dangerousGoodsList.tableHeader.operatingInstruction"
          ),
          value: "dangerousGoodsListOperatingInstruction",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("riskAssessment.tableHeader.proofed"),
          value: "dangerousGoodProofStatus",
          sortable: false,
        },
        {
          text: "",
          value: "dangerousGoodsListAction",
          align: "right",
          sortable: false,
          width: "200px",
        },
      ];
    },
    tableData() {
      return {
        tableComponentSlots: [
          {
            id: getUniqueId(),
            slotName: "item.dangerousGoodsListPictograms",
            componentName: "dangerousGoodsListPictograms",
          },
          {
            id: getUniqueId(),
            slotName: "item.dangerousGoodsListHAndPSentences",
            componentName: "dangerousGoodsListHAndPSentences",
          },
          {
            id: getUniqueId(),
            slotName: "item.storageClassification",
            componentName: "dangerousGoodsListStorageClassification",
          },
          {
            id: getUniqueId(),
            slotName: "item.fss7Relevant",
            componentName: "dangerousGoodsListFss7",
          },
          {
            id: getUniqueId(),
            slotName: "item.dangerousGoodsListOperatingInstruction",
            componentName: "dangerousGoodsListOperatingInstruction",
          },
          {
            id: getUniqueId(),
            slotName: "item.dangerousGoodsListSafetyDataSheet",
            componentName: "dangerousGoodsListSafetyDataSheet",
          },
          {
            id: getUniqueId(),
            slotName: "item.dangerousGoodProofStatus",
            componentName: "dangerousGoodProofStatus",
          },
          {
            id: getUniqueId(),
            slotName: "item.dangerousGoodsListAction",
            componentName: "dangerousGoodsListAction",
            config: { currentUserId: this.currentUserId },
          },
        ],
        itemKey: "_id",
        uriPartPrepend: "dangerousGood",
        uriPartAppend: `&dangerousGoodStorageLocation=${this.$route.params.storageLocationId}`,
        populate: ["operatingInstruction", "betriebsanweisung"],
        sort: "identifier",
        editFormPushName: "dangerousGoodsListForm",
      };
    },
  },
  data: () => ({
    tableSettings: {
      title: "",
      showSelect: true,
      showHeaderToolbar: true,
      headerButtons: [
        {
          title: "multiUser.button",
          color: "primary",
          action: "openMultiUserDialog",
          icon: "mdi-swap-horizontal",
          iconDark: true,
          dark: true,
        },
        {
          title: "dangerousGoods.sdsImport.button",
          color: "primary",
          action: "openSdsImport",
          icon: "mdi-file-upload-outline",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "dangerousGoods.dangerousGoodsList.export.button",
          color: "primary",
          action: "openExport",
          icon: "mdi-file-excel-outline",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "dangerousGoods.dangerousGoodsList.import.button",
          color: "grey",
          action: "openImport",
          icon: "download",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "dangerousGoods.dangerousGoodsList.addSubstance",
          color: "secondary",
          action: "newDocument",
          icon: "add",
          iconDark: true,
          dark: true,
          outlined: true,
        },
      ],
      multipleSelectButtons: [
        {
          title: "dangerousGoods.dangerousGoodsList.bulk.moveButton",
          color: "primary",
          action: "openBulkMove",
          icon: "content_paste_go",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "dangerousGoods.dangerousGoodsList.bulk.deleteButton",
          color: "error",
          action: "confirmBulkDelete",
          icon: "delete",
          iconDark: true,
          dark: true,
          outlined: true,
        },
      ],
    },
    bulkMove: {
      item: {},
      loading: false,
      targets: [],
      ids: [],
      dialog: {
        open: false,
        title: "dangerousGoods.dangerousGoodsList.bulk.moveTitle",
        description: "",
      },
    },
    bulkConfirm: {
      open: false,
      loading: false,
      title: "",
      message: "",
      ids: [],
    },
    currentClientId: "",
    isMultiUser: false,
    multiUserOpen: false,
    importDialog: { open: false },
    importing: false,
    currentUserId: "",
    transferClients: [],
    transferItems: [],
  }),
  async created() {
    const user = await this.$store.dispatch("getUserInfo");
    this.currentClientId = user?.client?._id || "";
    this.currentUserId = user?._id || "";
    this.isMultiUser = user?.role === "MultiUser";

    const res = await this.$store.dispatch("getAllDangerousGoods", {
      storageLocationId: this.$route.params.storageLocationId,
    });
    this.transferItems = res.data || res;

    if (!this.isMultiUser) {
      this.tableSettings.headerButtons =
        this.tableSettings.headerButtons.filter(
          (btn) => btn.action !== "openMultiUserDialog"
        );
    }

    if (!SDS_IMPORT_ENABLED) {
      this.tableSettings.headerButtons =
        this.tableSettings.headerButtons.filter(
          (btn) => btn.action !== "openSdsImport"
        );
    }
  },
  methods: {
    reloadDataFunc() {
      this.$refs.tableComponent.getData();
    },
    async openBulkMove(selectedItems) {
      this.bulkMove.ids = (selectedItems || []).map((i) => i._id);
      const res = await this.$store.dispatch(
        "getAllDangerousGoodsStorageLocations"
      );
      const locations = (res && res.data) || res || [];
      this.bulkMove.targets = locations
        .map((l) => ({ id: l.id || l._id, name: l.name }))
        .filter(
          (t) => String(t.id) !== String(this.$route.params.storageLocationId)
        );
      this.bulkMove.dialog.description = this.$t(
        "dangerousGoods.dangerousGoodsList.bulk.moveText",
        { count: this.bulkMove.ids.length }
      );
      this.bulkMove.dialog.open = true;
    },
    async handleBulkMoveConfirm(targetId) {
      try {
        this.bulkMove.loading = true;
        await this.$store.dispatch("bulkMoveDangerousGoods", {
          ids: this.bulkMove.ids,
          dangerousGoodStorageLocation: targetId,
        });
        this.bulkMove.dialog.open = false;
        this.onBulkActionDone();
      } finally {
        this.bulkMove.loading = false;
      }
    },
    confirmBulkDelete(selectedItems) {
      const ids = (selectedItems || []).map((i) => i._id);
      this.bulkConfirm = {
        open: true,
        loading: false,
        title: this.$t("dangerousGoods.dangerousGoodsList.bulk.deleteTitle"),
        message: this.$t(
          "dangerousGoods.dangerousGoodsList.bulk.deleteConfirm",
          {
            count: ids.length,
          }
        ),
        ids,
      };
    },
    async runBulkDelete() {
      this.bulkConfirm.loading = true;
      try {
        await this.$store.dispatch(
          "bulkDeleteDangerousGoods",
          this.bulkConfirm.ids
        );
        this.bulkConfirm.open = false;
        this.onBulkActionDone();
      } finally {
        this.bulkConfirm.loading = false;
      }
    },
    onBulkActionDone() {
      if (this.$refs.tableComponent) {
        this.$refs.tableComponent.clearSelectItems();
      }
      this.reloadDataFunc();
    },
    newDocument() {
      this.$router.push({
        name: this.tableData.editFormPushName,
        params: { id: "1", mode: "new", data: 1 },
      });
    },
    openExport() {
      this.$refs.exportDialog.open();
    },
    openImport() {
      this.importDialog.open = true;
    },
    async importData(file) {
      if (!file || !file.content) return;
      this.importing = true;
      try {
        await this.$store.dispatch("createDangerousGoodImport", {
          file: file.content,
          dangerousGoodStorageLocation: this.$route.params.storageLocationId,
        });
        this.importDialog.open = false;
        this.reloadDataFunc();
      } finally {
        this.importing = false;
      }
    },
    openSdsImport() {
      if (!this.$refs.sdsImportDialog) return;
      this.$refs.sdsImportDialog.open();
    },
    downloadPdf() {},

    async openMultiUserDialog() {
      try {
        const res = await this.$store.dispatch("getTransferClients");
        this.transferClients = res.data || [];
      } catch (e) {
        return;
      }
      this.multiUserOpen = true;
    },

    async executeTransfer(payload) {
      try {
        const result = await this.$store.dispatch("transferEntities", payload);

        const parts = [];
        if (result.totalSuccess > 0) {
          parts.push(
            this.$t("multiUser.result.success", {
              number: result.totalSuccess,
            })
          );
        }
        if (result.totalConflict > 0) {
          parts.push(
            this.$t("multiUser.result.error", {
              number: result.totalConflict,
            })
          );
        }
        if (result.totalError > 0) {
          parts.push(
            this.$t("multiUser.result.error", { number: result.totalError })
          );
        }

        this.$store.commit("CREATE_SNACKBAR", parts.join(" · "));

        if (payload.action === "move" && result.totalSuccess > 0) {
          this.reloadDataFunc();
        }
      } catch (e) {
        console.error("Transfer failed:", e);
      }
    },
  },
};
</script>
